import type { Dispatch, SetStateAction } from "react";
import { Box, IconButton } from "@mui/joy";
import type { Training } from "../../api/training/Training.typed";
import { useAuth } from "../../contexts/api";
import { userDisplayName } from "../../utils/userHelper";
import { SDate, SMissing } from "../../styles/StyledMUI";
import DarkTypography from "./DarkTypography";
import DarkDatePicker from "./DarkDatePicker";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

interface DatesComponentProps {
  training: Training;
  theory: Dayjs | null;
  rifap: Dayjs | null;
  setTheory: Dispatch<SetStateAction<Dayjs | null>>;
  setRifap: Dispatch<SetStateAction<Dayjs | null>>;
  updateTraining?: (training: Training) => Promise<void>;
  getTrainingsByTemplateId?: (templateId: string) => Promise<Training[] | undefined>;
  setTrainings?: Dispatch<SetStateAction<Training[] | undefined>>;
  isOpenDates: boolean;
  status?: number; // 1 = en cours, 2 = terminé
  /** When true, render as Box instead of table row (for card layouts) */
  standalone?: boolean;
}

// Utility function to check if all required dates are filled
export const areAllDatesValid = (
  training: Training,
  theory: Dayjs | null,
  rifap: Dayjs | null,
): boolean => {
  const needsTheory = training.NeedsTheory;
  const needsRifap = training.NeedsRifap;

  // If no dates are required, return false (no badge needed)
  if (!needsTheory && !needsRifap) return false;

  // Check if all required dates are filled and valid
  const theoryValid = !needsTheory || (theory !== null && theory.isValid());
  const rifapValid = !needsRifap || (rifap !== null && rifap.isValid());

  return theoryValid && rifapValid;
};

export default function DatesComponent({
  training,
  theory,
  rifap,
  setTheory,
  setRifap,
  updateTraining,
  getTrainingsByTemplateId,
  setTrainings,
  isOpenDates,
  status,
  standalone,
}: DatesComponentProps) {
  const { user } = useAuth();
  const maxDate = dayjs().format("YYYY-MM-DD");

  // Dates are only editable when status is "in progress" (1) or "completed" (2)
  const isEditable = status === 1 || status === 2;

  const handleDateChange = (date: string, type: "theory" | "rifap") => {
    const utcDate = dayjs(date).utc();

    if (type === "theory") setTheory(utcDate);
    else setRifap(utcDate);

    const { UpdatedAt: _updatedAt, ...trainingPayload } = training;
    const updatedTraining: Training = {
      ...trainingPayload,
      Theory: type === "theory" ? utcDate.toISOString() : theory?.toISOString() || "",
      RifapDate: type === "rifap" ? utcDate.toISOString() : rifap?.toISOString() || "",
      UpdatedBy: userDisplayName(user),
    };

    if (updateTraining && getTrainingsByTemplateId && setTrainings) {
      updateTraining(updatedTraining)
        .then(() => getTrainingsByTemplateId(String(training.TrainingTemplateID)))
        .then((ts) => ts != null && setTrainings(ts));
    }
  };

  const handleResetDate = (type: "theory" | "rifap") => {
    if (type === "theory") setTheory(null);
    else setRifap(null);

    const { UpdatedAt: _updatedAt, ...trainingPayload } = training;
    const updatedTraining: Training = {
      ...trainingPayload,
      Theory: type === "theory" ? "" : theory?.toISOString() || "",
      RifapDate: type === "rifap" ? "" : rifap?.toISOString() || "",
      UpdatedBy: userDisplayName(user),
    };

    if (updateTraining && getTrainingsByTemplateId && setTrainings) {
      updateTraining(updatedTraining)
        .then(() => getTrainingsByTemplateId(String(training.TrainingTemplateID)))
        .then((ts) => ts != null && setTrainings(ts));
    }
  };

  const content = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        marginTop: 1,
        marginBottom: 1,
        flexWrap: "wrap",
      }}
    >
      <SDate />

      {training.NeedsTheory && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DarkTypography
            level="body-sm"
            sx={{ minWidth: { xs: 0, sm: "120px" }, fontWeight: 600 }}
          >
            Cours théorique :
          </DarkTypography>
          <DarkDatePicker
            value={theory ? theory.tz(dayjs.tz.guess()).format("YYYY-MM-DD") : ""}
            onChange={(v) => handleDateChange(v, "theory")}
            max={maxDate}
            disabled={!isEditable}
            sx={{ width: { xs: "120px", sm: "140px" } }}
          />
          {theory && isEditable && (
            <IconButton size="sm" onClick={() => handleResetDate("theory")}>
              <SMissing />
            </IconButton>
          )}
        </Box>
      )}

      {training.NeedsTheory && training.NeedsRifap && (
        <DarkTypography level="body-sm" colorVariant="muted" sx={{ mx: { xs: 0.5, sm: 1 } }}>
          •
        </DarkTypography>
      )}

      {training.NeedsRifap && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DarkTypography level="body-sm" sx={{ minWidth: { xs: 0, sm: "60px" }, fontWeight: 600 }}>
            RIFAP :
          </DarkTypography>
          <DarkDatePicker
            value={rifap ? rifap.tz(dayjs.tz.guess()).format("YYYY-MM-DD") : ""}
            onChange={(v) => handleDateChange(v, "rifap")}
            max={maxDate}
            disabled={!isEditable}
            sx={{ width: { xs: "120px", sm: "140px" } }}
          />
          {rifap && isEditable && (
            <IconButton size="sm" onClick={() => handleResetDate("rifap")}>
              <SMissing />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );

  if (standalone) {
    return <Box sx={{ py: 1 }}>{content}</Box>;
  }
  return (
    <tr>
      <td colSpan={12} style={{ border: "none" }}>
        {content}
      </td>
    </tr>
  );
}
